import React, { useState } from 'react';
import { FileText } from 'lucide-react';
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage';
import { WorkEnvironmentRisk, PhotoModel } from '../../types/protocols';
import { FileAttachment } from '../../types/attachments';
import { useUsersData } from '../../Helpers/UsersContext';
import PhotoGallery from '../../Components/PhotoGallery';
import '../../Components/WarningViewComponent.css';

interface WorkEnvironmentRiskSectionProps {
    buildingKey: string;
    risk: WorkEnvironmentRisk | undefined;
    onPhotoClick?: (photo: PhotoModel, url: string, allPhotos: { [key: string]: PhotoModel }) => void;
    onDocumentClick?: (url: string, fileName: string, mimeType: string) => void;
    /** Key of the risk photo currently shown in the preview pane, to highlight its thumbnail. */
    selectedPhotoKey?: string;
}

const AlertIcon: React.FC<{ size: string }> = ({ size }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
);

const WorkEnvironmentRiskSection: React.FC<WorkEnvironmentRiskSectionProps> = ({
    buildingKey,
    risk,
    onPhotoClick,
    onDocumentClick,
    selectedPhotoKey,
}) => {
    const { users } = useUsersData();
    const [isExpanded, setIsExpanded] = useState(false);

    const getUserName = (uid: string | null): string => {
        if (!uid) return 'Okänd användare';
        const user = users?.find(u => u.key === uid);
        return user?.name || 'Okänd användare';
    };

    const handleOpenAttachment = async (attachment: FileAttachment) => {
        try {
            const storage = getStorage();
            const fileRef = storageRef(storage, attachment.storagePath);
            const url = await getDownloadURL(fileRef);
            if (onDocumentClick) {
                onDocumentClick(url, attachment.fileName, attachment.mimeType);
            } else {
                window.open(url, '_blank');
            }
        } catch (err) {
            console.error('Failed to open attachment:', err);
        }
    };

    const handleImageClick = (photo: PhotoModel, url: string, _index: number) => {
        if (onPhotoClick && risk?.photos) {
            onPhotoClick(photo, url, risk.photos);
        }
    };

    if (!risk) {
        return null;
    }

    const hasPhotos = risk.photos && Object.keys(risk.photos).length > 0;
    const hasAttachments = risk.attachments && Object.keys(risk.attachments).length > 0;

    return (
        <div className="hazard-section" style={{ maxWidth: 'none' }}>
            <div className="alert-box alert-box-enhanced hazard-text-color-yellow">
                <div
                    className="alert-header"
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <AlertIcon size="28" />
                    <h4 className="alert-title hazard-text-color-yellow">
                        Arbetsmiljörisk
                    </h4>
                    <svg
                        className={`ml-auto transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <path d="M6 9l6 6 6-6" />
                    </svg>
                </div>

                {isExpanded && (
                    <div className="alert-content">
                        {/* Description */}
                        {risk.description?.value && (
                            <div style={{ marginBottom: '12px' }}>
                                <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{risk.description.value}</p>
                                {risk.description.updatedBy && (
                                    <small style={{ color: '#888', display: 'block', marginTop: '4px' }}>
                                        Ändrad av {getUserName(risk.description.updatedBy)} {risk.description.updatedWhen}
                                    </small>
                                )}
                            </div>
                        )}

                        {/* Photos */}
                        {hasPhotos && (
                            <div style={{ marginBottom: '12px' }}>
                                <h6 style={{ marginBottom: '8px' }}>Foton</h6>
                                <PhotoGallery
                                    photos={risk.photos!}
                                    buildingKey={buildingKey}
                                    onImageClick={handleImageClick}
                                    thumbnailSize={80}
                                    selectedPhotoKey={selectedPhotoKey}
                                />
                            </div>
                        )}

                        {/* Attachments */}
                        {hasAttachments && (
                            <div>
                                <h6 style={{ marginBottom: '8px' }}>Filer</h6>
                                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                    {Object.entries(risk.attachments!).map(([key, attachment]) => (
                                        <li
                                            key={key}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '6px 0',
                                                borderBottom: '1px solid #eee',
                                            }}
                                        >
                                            <span
                                                style={{ cursor: 'pointer', color: '#0d6efd', display: 'flex', alignItems: 'center', gap: '6px' }}
                                                onClick={() => handleOpenAttachment(attachment)}
                                                title="Öppna fil"
                                            >
                                                <FileText size={14} />
                                                {attachment.fileName}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default WorkEnvironmentRiskSection;
